import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Unlink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Empty, fmtD, pInitials, LINK_STATUSES } from "./teamShared";
import { translateFr as tfr } from "@/i18n";

export default function TeamAthletes({ cid, onChanged }) {
  const [links, setLinks] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("Tous");
  const [linkOpen, setLinkOpen] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  const load = useCallback(() => {
    api.get(`/clubs/${cid}/athletes`).then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (links || []).filter((l) =>
    (statusF === "Tous" || l.status === statusF) &&
    (!q || l.player.name.toLowerCase().includes(q.toLowerCase()))), [links, q, statusF]);

  const setLinkStatus = async (lid, status) => {
    try {
      await api.patch(`/clubs/${cid}/athletes/${lid}`, { status });
      load(); onChanged();
    } catch { toast.error(tfr("Mise à jour impossible")); }
  };
  const unlink = async () => {
    try {
      await api.delete(`/clubs/${cid}/athletes/${confirmUnlink.id}`);
      toast.success(tfr("Athlète dissocié de l'équipe"));
      setConfirmUnlink(null); load(); onChanged();
    } catch { toast.error(tfr("Dissociation impossible")); }
  };

  const stats = useMemo(() => {
    const ages = (links || []).map((l) => l.player.age).filter(Boolean);
    const ending = (links || []).filter((l) => l.status === "Fin de contrat").length;
    return { avg: ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : null, ending };
  }, [links]);

  if (!links) return <div className="h-48 rounded-2xl bg-muted/60 animate-pulse" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tfr("Rechercher un athlète…")} className="h-10 sm:max-w-xs" data-testid="ath-search" />
        <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {["Tous", ...LINK_STATUSES].map((s) => (
            <button key={s} onClick={() => setStatusF(s)} data-testid={`ath-filter-${s}`}
              className={cn("h-10 px-3 rounded-full border text-xs whitespace-nowrap shrink-0 transition-colors",
                statusF === s ? "border-athlynx/40 bg-athlynx/10 text-athlynx font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
              {tfr(s)}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={() => setLinkOpen(true)} className="h-10 gap-1.5 bg-athlynx text-white hover:bg-athlynx/90 sm:ml-auto" data-testid="ath-link-btn">
          <Plus className="h-3.5 w-3.5" /> Associer un athlète
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Empty text={links.length ? tfr("Aucun résultat pour ces filtres.") : tfr("Aucun athlète associé à cette équipe.")}
          cta={links.length ? null : tfr("Associer un athlète")} onClick={() => setLinkOpen(true)} testid="ath-empty" />
      ) : (
        <div className="rounded-2xl border border-border bg-card shadow-sm dark:shadow-none divide-y divide-border/70" data-testid="ath-list">
          {filtered.map((l, i) => (
            <div key={l.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`ath-row-${i}`}>
              <span className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">{pInitials(l.player.name)}</span>
              <div className="min-w-0 flex-1">
                <Link to={`/players/${l.player.id}`} className="text-sm font-semibold hover:text-athlynx transition-colors truncate block" data-testid={`ath-name-${i}`}>{l.player.name}</Link>
                <p className="text-[11px] text-muted-foreground truncate">
                  {[l.player.position, l.player.category, l.player.sport].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Select value={l.status} onValueChange={(v) => setLinkStatus(l.id, v)}>
                  <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`ath-status-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{LINK_STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
                </Select>
                <Link to={`/players/${l.player.id}`} aria-label={`Ouvrir la fiche de ${l.player.name}`}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
                <button onClick={() => setConfirmUnlink(l)} aria-label={`Dissocier ${l.player.name}`} data-testid={`ath-unlink-${i}`}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                  <Unlink className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground" data-testid="ath-stats">
        {links.length} {links.length > 1 ? tfr("athlètes liés") : tfr("athlète lié")}
        {stats.avg != null && ` · ${tfr("Âge moyen :")} ${stats.avg} ${tfr("ans")}`}
        {stats.ending > 0 && ` · ${stats.ending} ${tfr("fin(s) de contrat")}`}
      </p>

      <LinkAthleteDialog cid={cid} open={linkOpen} onOpenChange={setLinkOpen} existing={links.map((l) => l.player_id)}
        onLinked={() => { load(); onChanged(); }} />

      <Dialog open={!!confirmUnlink} onOpenChange={(v) => !v && setConfirmUnlink(null)}>
        <DialogContent className="max-w-sm" data-testid="ath-unlink-confirm">
          <DialogHeader><DialogTitle className="text-base">{tfr("Dissocier cet athlète ?")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Seule l'association avec l'équipe sera supprimée. La fiche de {confirmUnlink?.player?.name} est conservée.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirmUnlink(null)}>{tfr("Annuler")}</Button>
            <Button size="sm" className="bg-athlynx text-white hover:bg-athlynx/90" onClick={unlink} data-testid="ath-unlink-confirm-btn">Dissocier</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LinkAthleteDialog({ cid, open, onOpenChange, existing, onLinked }) {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState("Au club");
  const [startDate, setStartDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { api.get("/players").then((r) => setPlayers(r.data || [])); setSel(null); setQ(""); }
  }, [open]);

  const list = players.filter((p) => !existing.includes(p.id) && (!q || p.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 8);

  const submit = async () => {
    if (!sel || busy) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${cid}/athletes`, { player_id: sel.id, status, start_date: startDate });
      toast.success(`${sel.name} associé à l'équipe`);
      onOpenChange(false); onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Association impossible");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="link-athlete-dialog">
        <DialogHeader><DialogTitle className="text-base">{tfr("Associer un athlète")}</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tfr("Rechercher dans votre portefeuille…")} className="h-10" data-testid="link-athlete-search" />
        <div className="max-h-[200px] overflow-y-auto space-y-1">
          {list.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">{players.length ? "Aucun résultat" : "Aucun athlète disponible"}</p>}
          {list.map((p) => (
            <button key={p.id} onClick={() => setSel(p)} data-testid={`link-athlete-option-${p.id}`}
              className={cn("w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors",
                sel?.id === p.id ? "border-athlynx/50 bg-athlynx/10" : "border-border hover:bg-accent")}>
              <span className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold">{pInitials(p.name)}</span>
              <span className="min-w-0"><span className="block truncate font-medium">{p.name}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{[p.sport, p.club].filter(Boolean).join(" · ")}</span></span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium">{tfr("Statut")}</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-10 mt-1 text-xs" data-testid="link-athlete-status"><SelectValue /></SelectTrigger>
              <SelectContent>{LINK_STATUSES.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">{tfr("Date de début")}</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10 mt-1" data-testid="link-athlete-date" />
          </div>
        </div>
        <Button onClick={submit} disabled={!sel || busy} className="bg-athlynx text-white hover:bg-athlynx/90" data-testid="link-athlete-submit">
          Associer {sel ? sel.name : ""}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
